class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeAnimationEndHandler = null;
    this.closeFallbackTimeout = null;
    this.renderUpdateId = 0;
    this.closedDuringUpdate = false;
    this.handlePageShow = (event) => {
      if (
        event.persisted ||
        this.classList.contains('active') ||
        this.classList.contains('animate') ||
        this.classList.contains('is-closing') ||
        this.classList.contains('is-rendering') ||
        this.classList.contains('is-updating')
      ) {
        this.resetDrawerState();
      }
    };

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('submit', (event) => {
      if (event.target && event.target.id === 'CartDrawer-Form' && event.submitter?.name === 'checkout') {
        this.resetDrawerState();
      }
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.handlePageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active') && !this.classList.contains('is-closing')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const drawerInner = this.querySelector('.drawer__inner');
    if (this.closeAnimationEndHandler && drawerInner) {
      drawerInner.removeEventListener('transitionend', this.closeAnimationEndHandler);
      this.closeAnimationEndHandler = null;
    }
    if (this.closeFallbackTimeout) {
      window.clearTimeout(this.closeFallbackTimeout);
      this.closeFallbackTimeout = null;
    }
    this.classList.remove('is-closing');
    this.closedDuringUpdate = false;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate');
    drawerInner?.getBoundingClientRect();
    window.requestAnimationFrame(() => {
      this.classList.add('active');
    });

    this.addEventListener(
      'transitionend',
      (event) => {
        if (event.target !== this.querySelector('.drawer__inner') || !this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this.classList.contains('active') && !this.classList.contains('animate')) return;
    const drawerInner = this.querySelector('.drawer__inner');
    if (this.classList.contains('is-updating')) this.closedDuringUpdate = true;
    if (this.closeAnimationEndHandler && drawerInner) {
      drawerInner.removeEventListener('transitionend', this.closeAnimationEndHandler);
    }
    this.classList.add('is-closing');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    const finishClose = () => {
      if (this.closeFallbackTimeout) {
        window.clearTimeout(this.closeFallbackTimeout);
        this.closeFallbackTimeout = null;
      }
      if (this.closeAnimationEndHandler && drawerInner) {
        drawerInner.removeEventListener('transitionend', this.closeAnimationEndHandler);
      }
      this.closeAnimationEndHandler = null;
      document.body.classList.remove('overflow-hidden');
      this.classList.remove('animate', 'is-closing', 'is-rendering', 'is-updating');
    };
    if (drawerInner) {
      this.closeAnimationEndHandler = (event) => {
        if (event.target !== drawerInner || event.propertyName !== 'transform') return;
        finishClose();
      };
      drawerInner.addEventListener('transitionend', this.closeAnimationEndHandler);
      this.closeFallbackTimeout = window.setTimeout(finishClose, 260);
    } else {
      finishClose();
    }
  }

  resetDrawerState() {
    const drawerInner = this.querySelector('.drawer__inner');
    if (this.closeAnimationEndHandler && drawerInner) {
      drawerInner.removeEventListener('transitionend', this.closeAnimationEndHandler);
    }
    if (this.closeFallbackTimeout) {
      window.clearTimeout(this.closeFallbackTimeout);
    }
    this.closeAnimationEndHandler = null;
    this.closeFallbackTimeout = null;
    this.closedDuringUpdate = false;
    document.body.classList.remove('overflow-hidden');
    this.classList.remove('active', 'animate', 'is-closing', 'is-rendering', 'is-updating');
    this.querySelectorAll('.cart__items--disabled').forEach((element) => element.classList.remove('cart__items--disabled'));
    this.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasActive = this.classList.contains('active');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.classList.add('is-updating', 'is-rendering');
    this.productId = parsedState.id;
    const renderUpdateId = ++this.renderUpdateId;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    const shouldRemainClosed = this.closedDuringUpdate && !this.classList.contains('active');
    if (wasActive && !shouldRemainClosed) this.open();
    this.waitForDrawerMedia().then(() => {
      if (renderUpdateId !== this.renderUpdateId) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (renderUpdateId !== this.renderUpdateId) return;
          this.classList.remove('is-rendering', 'is-updating');
          this.closedDuringUpdate = false;
          if (!wasActive && !shouldRemainClosed) this.open();
        });
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  waitForDrawerMedia() {
    const images = [...this.querySelectorAll('#CartDrawer .cart-item__image')];
    if (!images.length) return Promise.resolve();

    const imageSettled = images.map((image) => {
      if (image.complete && image.naturalWidth !== 0) return Promise.resolve();
      if (image.decode) return image.decode().catch(() => undefined);

      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    });

    return Promise.race([
      Promise.all(imageSettled),
      new Promise((resolve) => window.setTimeout(resolve, 450)),
    ]);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
